// The SDK side of the shell↔SDK postMessage bridge. Origin-pinned in BOTH directions: inbound
// messages must come from the embedding parent AND an allowed origin; outbound messages name the
// allowed origins explicitly (never "*"). Per the Phase-1a COOP finding, no window handle is ever
// trusted or stored beyond `window.parent` as a postMessage target.
import { isShellMessage, type SdkToShellMessage, type ShellToSdkMessage } from "@redliner/protocol";
import type { ResolvedConfig } from "./config.js";

export interface BridgeHandlers {
  onPickStart(requestId: string): void;
  onPickCancel(requestId: string): void;
}

export interface Bridge {
  framed: boolean;
  post(msg: SdkToShellMessage): void;
  start(): void;
  stop(): void;
}

export function createBridge(
  cfg: ResolvedConfig,
  handlers: BridgeHandlers,
  // injectable for tests; production always uses the real frame relationship
  env: { parent: Window; self: Window } = { parent: window.parent, self: window },
): Bridge {
  const framed = env.self !== env.parent;

  const post = (msg: SdkToShellMessage) => {
    if (!framed) return;
    for (const origin of cfg.allowedParents) env.parent.postMessage(msg, origin);
  };

  const onMessage = (e: MessageEvent) => {
    if (e.source !== env.parent || !cfg.allowedParents.includes(e.origin)) return;
    if (!isShellMessage(e.data)) return;
    const msg: ShellToSdkMessage = e.data;
    switch (msg.type) {
      case "redliner:shell:ping":
        post({ v: 1, type: "redliner:sdk:pong" });
        break;
      case "redliner:shell:pick-start":
        handlers.onPickStart(msg.requestId);
        break;
      case "redliner:shell:pick-cancel":
        handlers.onPickCancel(msg.requestId);
        break;
    }
  };

  return {
    framed,
    post,
    start: () => env.self.addEventListener("message", onMessage),
    stop: () => env.self.removeEventListener("message", onMessage),
  };
}
